import React, { useCallback, useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
    ArrowLeft, Calendar, User, Clock, Scissors, CreditCard, Edit, X, Save, Loader2, UserCheck, AlertCircle, CheckCircle2,
} from 'lucide-react';
import styles from './BookingDetails.module.css';
import { SMIIFY_API } from '../../../config/api';
import useAccess from '../../../hooks/useAccess';
import { getBillDate, getCreatedAt, toLocalYMD } from '../../../utils/billDates';

const INVOICES_API = `${SMIIFY_API}/invoices`;


/** 9876543210 -> 98••••••10 */
const maskPhone = (p) => { const s = String(p || ''); return s.length >= 6 ? `${s.slice(0, 2)}${'•'.repeat(s.length - 4)}${s.slice(-2)}` : '••••'; };

const formatMoney = (value) => new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', minimumFractionDigits: 2 }).format(Number(value) || 0);
const fmtDate = (d) => (d && !Number.isNaN(d.getTime()) ? new Intl.DateTimeFormat('en-IN', { day: '2-digit', month: 'long', year: 'numeric' }).format(d) : '—');
const fmtTime = (d) => (d && !Number.isNaN(d.getTime()) ? new Intl.DateTimeFormat('en-IN', { hour: '2-digit', minute: '2-digit', hour12: true }).format(d) : '—');
const fmtDateTime = (v) => (v ? new Date(v).toLocaleString('en-IN', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit', hour12: true }) : '—');
const toHHMM = (d) => `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;

const STATUS_VIEW = {
    completed: { label: 'COMPLETED', color: 'var(--h-color-success-600, #007a49)' },
    draft: { label: 'DRAFT', color: 'var(--h-color-neutral-600, #58585e)' },
    cancelled: { label: 'CANCELLED', color: 'var(--h-color-danger-600, #be1025)' },
};

const BookingDetails = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const token = localStorage.getItem('smiify_token');
    const { can } = useAccess();
    const canChangeBillDate = can('billing.change_bill_date');
    const canSeePhone = can('customers.view_phone');

    const [booking, setBooking] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [loadError, setLoadError] = useState('');
    const [activeTab, setActiveTab] = useState('schedule');
    const [notice, setNotice] = useState('');

    // Change-date modal
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [modalError, setModalError] = useState('');
    const [apptDate, setApptDate] = useState('');
    const [apptTime, setApptTime] = useState('');

    const headers = useCallback((json = false) => ({
        ...(json ? { 'Content-Type': 'application/json' } : {}),
        Authorization: `Bearer ${token}`,
        ...(localStorage.getItem('smiify_active_store_id') ? { 'x-store-id': localStorage.getItem('smiify_active_store_id') } : {}),
    }), [token]);

    const fetchBooking = useCallback(async () => {
        setIsLoading(true);
        setLoadError('');
        try {
            const res = await fetch(`${INVOICES_API}/${id}`, { headers: headers() });
            const json = await res.json().catch(() => ({}));
            if (res.ok && json.success && json.data) setBooking(json.data);
            else setLoadError(json.message || 'Bill not found.');
        } catch {
            setLoadError('Cannot reach the server. Try again in a minute.');
        } finally {
            setIsLoading(false);
        }
    }, [id, headers]);

    useEffect(() => { if (id && token) fetchBooking(); }, [id, token, fetchBooking]);

    const openModal = () => {
        const current = getBillDate(booking);
        setApptDate(toLocalYMD(current));
        setApptTime(toHHMM(current));
        setModalError('');
        setIsModalOpen(true);
    };

    const handleSaveDate = async () => {
        setModalError('');
        if (!apptDate || !apptTime) { setModalError('Choose a date and a time.'); return; }
        const [y, m, d] = apptDate.split('-').map(Number);
        const [hh, mm] = apptTime.split(':').map(Number);
        const chosen = new Date(y, m - 1, d, hh, mm);
        if (Number.isNaN(chosen.getTime())) { setModalError('Choose a valid date and time.'); return; }
        if (chosen.getTime() > Date.now() + 60000) { setModalError('A bill cannot be completed in the future. Choose now or earlier.'); return; }

        setIsSaving(true);
        try {
            const res = await fetch(`${INVOICES_API}/${id}/reschedule`, {
                method: 'PATCH',
                headers: headers(true),
                body: JSON.stringify({ newDate: chosen.toISOString(), tzOffset: new Date().getTimezoneOffset() }),
            });
            const json = await res.json().catch(() => ({}));
            if (!res.ok || json.success === false) { setModalError(json.message || 'Could not change the date.'); return; }
            setIsModalOpen(false);
            setBooking(json.data || booking);
            setNotice(`Moved to ${fmtDate(chosen)}, ${fmtTime(chosen)}. Dashboard, invoice history and all reports now show it on this date.`);
            setTimeout(() => setNotice(''), 8000);
            if (!json.data) fetchBooking();
        } catch {
            setModalError('Cannot reach the server.');
        } finally {
            setIsSaving(false);
        }
    };

    if (isLoading) {
        return (
            <div style={{ display: 'flex', height: '100vh', alignItems: 'center', justifyContent: 'center', background: 'var(--h-color-neutral-50)' }}>
                <Loader2 className={styles.spin} size={30} color="var(--h-color-primary-600)" />
            </div>
        );
    }

    if (loadError || !booking) {
        return (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12, height: '100vh', alignItems: 'center', justifyContent: 'center', fontFamily: 'var(--font-family-base, Nunito, sans-serif)' }}>
                <strong style={{ color: 'var(--h-color-danger-600, #be1025)' }}>{loadError || 'Bill not found.'}</strong>
                <button type="button" className={styles.backBtn} onClick={() => navigate('/booking-history')}><ArrowLeft size={14} /> Back to bookings</button>
            </div>
        );
    }

    const status = String(booking.status || 'completed').toLowerCase();
    const statusView = STATUS_VIEW[status] || STATUS_VIEW.completed;
    const isCompleted = status === 'completed';
    const billDate = getBillDate(booking);
    const createdAt = getCreatedAt(booking);
    const history = [...(booking.dateChangeHistory || [])].reverse();
    const canChangeDate = status !== 'cancelled' && canChangeBillDate;

    return (
        <div className={styles.pageContainer}>
            {/* Header */}
            <div className={styles.header}>
                <div className={styles.headerLeft}>
                    <button type="button" className={styles.backBtn} onClick={() => navigate('/booking-history')}><ArrowLeft size={14} /> Back</button>
                    <div className={styles.titleBox}>
                        <h1 className={styles.title}>Booking Record</h1>
                        <span className={styles.bookingId}>Bill No: {booking.invoiceNo || 'N/A'}</span>
                    </div>
                </div>
                {canChangeDate && (
                    <button type="button" className={styles.primaryActionBtn} onClick={openModal}>
                        <Edit size={14} /> {isCompleted ? 'Change completion date' : 'Change date'}
                    </button>
                )}
            </div>

            {notice && (
                <div role="status" style={{ display: 'flex', alignItems: 'center', gap: 8, margin: '0 0 12px', padding: '9px 12px', borderRadius: 8, background: 'var(--h-color-success-50, #f1f9f3)', color: 'var(--h-color-success-700, #005432)', fontSize: 12, fontWeight: 700 }}>
                    <CheckCircle2 size={14} /> {notice}
                </div>
            )}

            <div className={styles.contentArea}>
                <div className={styles.tabsList} role="tablist">
                    {[
                        ['schedule', 'Date & Status', Calendar],
                        ['client', 'Client', User],
                        ['services', 'Services & Staff', Scissors],
                        ['finance', 'Payment', CreditCard],
                    ].map(([key, label, Icon]) => (
                        <button type="button" role="tab" aria-selected={activeTab === key} key={key} className={`${styles.tabBtn} ${activeTab === key ? styles.activeTab : ''}`} onClick={() => setActiveTab(key)}>
                            <Icon size={16} /> {label}
                        </button>
                    ))}
                </div>

                <div className={styles.tabContent}>
                    {/* Date & status */}
                    {activeTab === 'schedule' && (
                        <div>
                            <div className={styles.tabGraphicHeader}>
                                <div className={styles.tabIconBox}><Calendar size={24} /></div>
                                <div className={styles.tabTitle}>
                                    <h2>Bill date</h2>
                                    <p>{isCompleted ? 'Reports and the dashboard count this bill on its completion date.' : 'This bill is not completed yet.'}</p>
                                </div>
                            </div>
                            <div className={styles.detailGrid}>
                                <div className={styles.detailCard}>
                                    <div className={styles.detailRow}><span>{isCompleted ? 'Completed on:' : 'Bill date:'}</span> <strong>{fmtDate(billDate)}</strong></div>
                                    <div className={styles.detailRow}><span>Time:</span> <strong>{fmtTime(billDate)}</strong></div>
                                    <div className={styles.detailRow}><span>Created on:</span> <strong>{fmtDate(createdAt)}, {fmtTime(createdAt)}</strong></div>
                                    <div className={styles.detailRow}><span>Status:</span> <strong style={{ color: statusView.color }}>{statusView.label}</strong></div>
                                </div>

                                <div className={styles.detailCard}>
                                    <h4 style={{ margin: '0 0 10px', fontSize: '13px', fontWeight: 800, color: 'var(--h-color-neutral-800)' }}>Date changes</h4>
                                    {history.length === 0 ? (
                                        <p style={{ margin: 0, fontSize: 12, color: 'var(--h-color-neutral-500)', fontWeight: 600 }}>The date has not been changed.</p>
                                    ) : history.map((h, i) => (
                                        <div key={`${h.at}-${i}`} className={styles.detailRow} style={{ alignItems: 'flex-start' }}>
                                            <span>{fmtDateTime(h.from)} to</span>
                                            <strong style={{ textAlign: 'right' }}>
                                                {fmtDateTime(h.to)}
                                                <small style={{ display: 'block', fontWeight: 600, color: 'var(--h-color-neutral-500)' }}>
                                                    {h.byName ? `by ${h.byName}, ` : ''}{fmtDateTime(h.at)}
                                                </small>
                                            </strong>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Client */}
                    {activeTab === 'client' && (
                        <div>
                            <div className={styles.tabGraphicHeader}>
                                <div className={styles.tabIconBox}><User size={24} /></div>
                                <div className={styles.tabTitle}><h2>Customer</h2><p>Who took these services.</p></div>
                            </div>
                            <div className={styles.detailGrid}>
                                <div className={styles.detailCard}>
                                    <div className={styles.detailRow}><span>Name:</span> <strong>{booking.customer?.name || 'Walk-in Customer'}</strong></div>
                                    <div className={styles.detailRow}><span>Mobile:</span> <strong>{booking.customer?.phone ? `+91 ${canSeePhone ? booking.customer.phone : maskPhone(booking.customer.phone)}` : 'N/A'}</strong></div>
                                    <div className={styles.detailRow}><span>Customer ID:</span> <strong>{booking.customer?.customerId ? String(booking.customer.customerId).slice(-6).toUpperCase() : 'Guest'}</strong></div>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Services */}
                    {activeTab === 'services' && (
                        <div>
                            <div className={styles.tabGraphicHeader}>
                                <div className={styles.tabIconBox}><Scissors size={24} /></div>
                                <div className={styles.tabTitle}><h2>Services & staff</h2><p>What was done and by whom.</p></div>
                            </div>
                            <div className={styles.serviceList}>
                                {booking.items?.length ? booking.items.map((srv, idx) => {
                                    const staffList = srv.staff?.length ? srv.staff.map((s) => s.name).join(', ') : 'No staff';
                                    const consultantList = srv.consultedBy?.length ? srv.consultedBy.map((c) => c.name).join(', ') : 'None';
                                    return (
                                        <div key={idx} className={styles.serviceItem}>
                                            <div className={styles.serviceInfo}>
                                                <span className={styles.serviceName}>{srv.itemName}</span>
                                                <div className={styles.staffTags}>
                                                    <span className={styles.staffTag}><UserCheck size={12} /> Served by: {staffList}</span>
                                                    <span className={styles.staffTag}><Clock size={12} /> Consulted: {consultantList}</span>
                                                </div>
                                            </div>
                                            <div className={styles.servicePrice}>
                                                <strong>{formatMoney(srv.amount)}</strong>
                                                <span>Qty: {srv.qty}</span>
                                            </div>
                                        </div>
                                    );
                                }) : (
                                    <p style={{ fontSize: '12px', color: 'var(--h-color-neutral-500)', fontWeight: 700 }}>No services on this bill.</p>
                                )}
                            </div>
                        </div>
                    )}

                    {/* Payment */}
                    {activeTab === 'finance' && (
                        <div>
                            <div className={styles.tabGraphicHeader}>
                                <div className={styles.tabIconBox}><CreditCard size={24} /></div>
                                <div className={styles.tabTitle}><h2>Billing & payments</h2><p>Amount and how it was paid.</p></div>
                            </div>
                            <div className={styles.detailGrid}>
                                <div className={styles.detailCard}>
                                    <h4 style={{ margin: '0 0 12px', fontSize: '13px', fontWeight: 800, color: 'var(--h-color-neutral-800)' }}>Invoice breakdown</h4>
                                    <div className={styles.detailRow}><span>Subtotal:</span> <strong>{formatMoney(booking.summary?.sumGross)}</strong></div>
                                    <div className={styles.detailRow}><span>Discount:</span> <strong>- {formatMoney((Number(booking.summary?.finalOverallDisc) || 0) + (Number(booking.summary?.sumItemDiscount) || 0))}</strong></div>
                                    <div className={styles.detailRow}><span>GST:</span> <strong>+ {formatMoney(booking.summary?.sumTax)}</strong></div>
                                    <div className={styles.detailRow} style={{ marginTop: '10px', paddingTop: '10px', borderTop: '2px solid var(--h-color-neutral-200)', fontSize: '14px', borderBottom: 'none' }}>
                                        <span style={{ color: 'var(--h-color-neutral-900)', fontWeight: 800 }}>Grand total:</span>
                                        <strong style={{ color: 'var(--h-color-success-600)', fontSize: '16px' }}>{formatMoney(booking.summary?.grandTotal)}</strong>
                                    </div>
                                </div>
                                <div className={styles.detailCard}>
                                    <h4 style={{ margin: '0 0 12px', fontSize: '13px', fontWeight: 800, color: 'var(--h-color-neutral-800)' }}>Payment methods</h4>
                                    {booking.payments?.cash > 0 && <div className={styles.detailRow}><span>Cash:</span> <strong>{formatMoney(booking.payments.cash)}</strong></div>}
                                    {booking.payments?.upi > 0 && <div className={styles.detailRow}><span>UPI:</span> <strong>{formatMoney(booking.payments.upi)}</strong></div>}
                                    {booking.payments?.card > 0 && <div className={styles.detailRow}><span>Card:</span> <strong>{formatMoney(booking.payments.card)}</strong></div>}
                                    {booking.payments?.wallet > 0 && <div className={styles.detailRow}><span>Khata wallet:</span> <strong>{formatMoney(booking.payments.wallet)}</strong></div>}
                                    {booking.payments?.prepaidCardAmount > 0 && <div className={styles.detailRow}><span>Prepaid card:</span> <strong>{formatMoney(booking.payments.prepaidCardAmount)}</strong></div>}
                                    {booking.paymentTracking?.totalPaid === 0 && Number(booking.summary?.grandTotal) > 0 && (
                                        <div className={styles.detailRow} style={{ border: 'none', color: 'var(--h-color-danger-600)' }}><span>Status:</span> <strong>Unpaid / due</strong></div>
                                    )}
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            </div>

            {/* Change date modal */}
            {isModalOpen && (
                <div className={styles.modalOverlay} role="presentation" onClick={() => !isSaving && setIsModalOpen(false)}>
                    <div className={styles.modalContent} role="dialog" aria-modal="true" aria-label="Change bill date" onClick={(e) => e.stopPropagation()}>
                        <div className={styles.modalHeader}>
                            <h3>{isCompleted ? 'Change completion date' : 'Change bill date'}</h3>
                            <button type="button" className={styles.closeBtn} onClick={() => setIsModalOpen(false)} aria-label="Close"><X size={16} /></button>
                        </div>
                        <div className={styles.modalBody}>
                            {modalError && (
                                <div role="alert" style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 12, padding: '8px 10px', borderRadius: 8, background: 'var(--h-color-danger-50, #fff4f3)', color: 'var(--h-color-danger-700, #870013)', fontSize: 12, fontWeight: 700 }}>
                                    <AlertCircle size={14} /> {modalError}
                                </div>
                            )}
                            <div className={styles.formGroup}>
                                <label className={styles.label} htmlFor="bd-date">New date</label>
                                <input id="bd-date" type="date" className={styles.input} value={apptDate} max={toLocalYMD(new Date())} onChange={(e) => setApptDate(e.target.value)} />
                            </div>
                            <div className={styles.formGroup} style={{ marginBottom: 0 }}>
                                <label className={styles.label} htmlFor="bd-time">Time</label>
                                <input id="bd-time" type="time" className={styles.input} value={apptTime} onChange={(e) => setApptTime(e.target.value)} />
                            </div>
                            {isCompleted && (
                                <p style={{ margin: '12px 0 0', fontSize: 11.5, lineHeight: 1.45, color: 'var(--h-color-neutral-600, #58585e)', fontWeight: 600 }}>
                                    The bill will move to this date on the dashboard, invoice history, collection, GST, staff and inventory reports. Its prepaid card entries move too.
                                </p>
                            )}
                        </div>
                        <div className={styles.modalFooter}>
                            <button type="button" className={styles.cancelBtn} onClick={() => setIsModalOpen(false)} disabled={isSaving}>Cancel</button>
                            <button type="button" className={styles.saveBtn} onClick={handleSaveDate} disabled={isSaving}>
                                {isSaving ? <Loader2 className={styles.spin} size={14} /> : <Save size={14} />}
                                {isSaving ? 'Saving...' : 'Save date'}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default BookingDetails;